import React, { useEffect, useState, Children, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../lib/supabase';
import { ChevronRight, ArrowLeft, CheckCircle2, Loader2, MessageCircle, X, Smartphone, UtensilsCrossed, BarChart3, Crown, Zap, QrCode } from 'lucide-react';
// --- Background Elements ---
const FLOATING_FOODS = ['🍔', '🍕', '🍣', '☕', '🍟', '🌮', '🍰', '🥗', '🍩', '🥑'];
const BACKGROUND_WORDS = ['Pedidos', 'WhatsApp', 'Delivery', 'Marketplace', 'KDS', 'Tracking', 'Operación', 'Tecnología', 'Restaurantes', 'Crecimiento', 'Control', 'Inventario'];
// --- Form Types ---
type BusinessType = 'Restaurante' | 'Cafetería' | 'Pizzería' | 'Comida rápida' | 'Panadería' | 'Otro';
type MainGoal = 'Más pedidos' | 'Mejor control de cocina' | 'Menú digital' | 'Delivery / express' | 'Orden y operación' | 'Todo lo anterior';
interface FormData {
  owner_name: string;
  restaurant_name: string;
  whatsapp: string;
  social_handle: string;
  business_type: BusinessType | '';
  uses_whatsapp_orders: boolean | null;
  has_digital_menu: boolean | null;
  main_goal: MainGoal[];
}
export function OnboardingPage() {
  const [step, setStep] = useState(0); // 0 = Hero, 1-5 = Form, 6 = Success
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    owner_name: '',
    restaurant_name: '',
    whatsapp: '',
    social_handle: '',
    business_type: '',
    uses_whatsapp_orders: null,
    has_digital_menu: null,
    main_goal: []
  });
  // --- Handlers ---
  const handleNext = () => setStep(s => Math.min(s + 1, 6));
  const handleBack = () => setStep(s => Math.max(s - 1, 0));
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      const cleanWhatsapp = formData.whatsapp.replace(/\D/g, '');
      if (!formData.owner_name || !formData.restaurant_name || !cleanWhatsapp || !formData.business_type) {
        throw new Error('Faltan campos obligatorios');
      }
      const {
        error
      } = await supabase.from('restaurant_applications').insert([{
        owner_name: formData.owner_name.trim(),
        restaurant_name: formData.restaurant_name.trim(),
        whatsapp: cleanWhatsapp,
        social_handle: formData.social_handle?.trim() || null,
        business_type: formData.business_type,
        uses_whatsapp_orders: formData.uses_whatsapp_orders ?? false,
        has_digital_menu: formData.has_digital_menu ?? false,
        main_goal: formData.main_goal.join(', ') || null,
        status: 'pending'
      }]);
      if (error) {
        console.error('❌ Error guardando aplicación:', error);
        throw error;
      }
      console.log('✅ Aplicación guardada correctamente');
      // Open WhatsApp with pre-filled message
      const message = encodeURIComponent(`Hola, acabo de aplicar a NOIZA.\nRestaurante: ${formData.restaurant_name}`);
      window.open(`https://wa.me/50688118105?text=${message}`, '_blank');
      setStep(6); // Success
    } catch (err: any) {
      console.error('❌ Error en submit:', err);
      setError('Hubo un error al enviar tu solicitud. Por favor intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };
  // --- Step Validation ---
  const isStepValid = () => {
    switch (step) {
      case 1:
        return formData.owner_name.trim().length > 0 && formData.restaurant_name.trim().length > 0;
      case 2:
        return formData.whatsapp.trim().length >= 8;
      case 3:
        return formData.business_type !== '';
      case 4:
        return formData.uses_whatsapp_orders !== null && formData.has_digital_menu !== null;
      case 5:
        return formData.main_goal.length > 0;
      default:
        return true;
    }
  };
  // --- Render Helpers ---
  const renderFloatingBackground = () => <div className="fixed inset-0 overflow-hidden pointer-events-none z-0" data-id="element-5455">
      {/* Dark gradient base */}
      <div className="absolute inset-0 bg-[#0B0B0B]" data-id="element-5456" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(201,163,78,0.15)_0%,rgba(11,11,11,1)_70%)]" data-id="element-5457" />

      {/* Floating Words */}
      {BACKGROUND_WORDS.map((word, i) => <motion.div key={`word-${i}`} className="absolute text-white/5 font-black text-4xl md:text-6xl lg:text-8xl whitespace-nowrap select-none" initial={{
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      rotate: -15 + Math.random() * 30
    }} animate={{
      x: [null, Math.random() * window.innerWidth],
      y: [null, Math.random() * window.innerHeight]
    }} transition={{
      duration: 40 + Math.random() * 40,
      repeat: Infinity,
      repeatType: 'reverse',
      ease: 'linear'
    }} data-id="element-5458">
          {word}
        </motion.div>)}

      {/* Floating Foods */}
      {FLOATING_FOODS.map((food, i) => <motion.div key={`food-${i}`} className="absolute text-4xl md:text-6xl opacity-40 blur-[1px]" initial={{
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      scale: 0.5 + Math.random() * 1
    }} animate={{
      y: [null, Math.random() * window.innerHeight],
      rotate: [0, 360]
    }} transition={{
      y: {
        duration: 20 + Math.random() * 20,
        repeat: Infinity,
        repeatType: 'reverse',
        ease: 'easeInOut'
      },
      rotate: {
        duration: 30 + Math.random() * 30,
        repeat: Infinity,
        ease: 'linear'
      }
    }} data-id="element-5459">
          {food}
        </motion.div>)}
    </div>;
  const renderHowItWorksModal = () => <AnimatePresence data-id="element-5460">
      {showHowItWorks && <motion.div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} data-id="element-5461">
          {/* Backdrop */}
          <motion.div className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowHowItWorks(false)} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} data-id="element-5462" />

          {/* Modal Content */}
          <motion.div className="relative z-10 w-full max-w-3xl mx-4 my-8 sm:my-16 bg-[#141414] border border-white/10 rounded-3xl overflow-hidden shadow-2xl" initial={{
        scale: 0.9,
        opacity: 0,
        y: 40
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.9,
        opacity: 0,
        y: 40
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} data-id="element-5463">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-[#141414]/95 backdrop-blur-md border-b border-white/10 px-6 py-5 flex items-center justify-between" data-id="element-5464">
              <div data-id="element-5465">
                <h2 className="text-2xl font-bold text-white" data-id="element-5466">
                  ¿Cómo funciona NOIZA?
                </h2>
                <p className="text-sm text-white/50 mt-1" data-id="element-5467">
                  Explicado de forma simple
                </p>
              </div>
              <button onClick={() => setShowHowItWorks(false)} className="p-2 hover:bg-white/10 rounded-xl transition-colors" data-id="element-5468">
                <X className="w-6 h-6 text-white/60" data-id="element-5469" />
              </button>
            </div>

            <div className="p-6 sm:p-8 space-y-10" data-id="element-5470">
              {/* What is NOIZA */}
              <div data-id="element-5471">
                <h3 className="text-lg font-bold text-[#C9A34E] mb-3 flex items-center gap-2" data-id="element-5472">
                  <Zap className="w-5 h-5" data-id="element-5473" />
                  ¿Qué es NOIZA?
                </h3>
                <p className="text-white/70 leading-relaxed" data-id="element-5474">
                  NOIZA es una plataforma digital para restaurantes. En vez de
                  recibir pedidos por WhatsApp de forma desordenada, tus
                  clientes escanean un{' '}
                  <strong className="text-white" data-id="element-5475">código QR</strong>, ven tu menú
                  en su celular, hacen su pedido, y este llega directo a tu
                  cocina organizado. Sin confusiones, sin errores, sin estrés.
                </p>
              </div>

              {/* How it works step by step */}
              <div data-id="element-5476">
                <h3 className="text-lg font-bold text-[#C9A34E] mb-4 flex items-center gap-2" data-id="element-5477">
                  <Smartphone className="w-5 h-5" data-id="element-5478" />
                  Paso a paso
                </h3>
                <div className="space-y-4" data-id="element-5479">
                  {[{
                num: '1',
                title: 'Subimos tu menú',
                desc: 'Nos pasas tu menú (fotos, precios, categorías) y nosotros lo montamos en la plataforma. Queda bonito y profesional.'
              }, {
                num: '2',
                title: 'Te damos un QR y un link',
                desc: 'Tu restaurante tiene su propia página web con tu menú. Tus clientes pueden acceder escaneando un QR en la mesa o desde un link en tus redes.'
              }, {
                num: '3',
                title: 'El cliente hace su pedido',
                desc: 'Tu cliente ve el menú, elige lo que quiere, personaliza opciones (tamaño, extras, salsas) y confirma su pedido desde su celular.'
              }, {
                num: '4',
                title: 'El pedido llega a tu cocina',
                desc: 'En tu pantalla de cocina (KDS) aparece el pedido automáticamente. Sin papelitos, sin gritos, sin confusiones. Lo marcas como listo cuando sale.'
              }, {
                num: '5',
                title: 'Tú controlas todo',
                desc: 'Desde tu panel de admin ves las ventas del día, métricas, puedes pausar la cocina si estás lleno, activar/desactivar platillos, y más.'
              }].map(step => <div key={step.num} className="flex gap-4" data-id="element-5480">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#C9A34E]/20 text-[#C9A34E] font-bold text-sm flex items-center justify-center" data-id="element-5481">
                        {step.num}
                      </div>
                      <div data-id="element-5482">
                        <p className="font-semibold text-white" data-id="element-5483">{step.title}</p>
                        <p className="text-sm text-white/60 mt-0.5" data-id="element-5484">
                          {step.desc}
                        </p>
                      </div>
                    </div>)}
                </div>
              </div>

              {/* Plans */}
              <div data-id="element-5485">
                <h3 className="text-lg font-bold text-[#C9A34E] mb-2 flex items-center gap-2" data-id="element-5486">
                  <Crown className="w-5 h-5" data-id="element-5487" />
                  Nuestros Planes
                </h3>
                <p className="text-white/50 text-sm mb-6" data-id="element-5488">
                  Elegí el que mejor se ajuste a tu restaurante. Siempre podés
                  escalar después.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-5489">
                  {/* Fase 1 */}
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-3" data-id="element-5490">
                    <div className="flex items-center gap-2" data-id="element-5491">
                      <QrCode className="w-5 h-5 text-[#C9A34E]" data-id="element-5492" />
                      <h4 className="font-bold text-white text-sm" data-id="element-5493">
                        Menú Digital Fase 1
                      </h4>
                    </div>
                    <p className="text-xs text-white/50 leading-relaxed" data-id="element-5494">
                      Lo esencial para arrancar digital.
                    </p>
                    <ul className="space-y-2 text-sm text-white/70" data-id="element-5495">
                      <li className="flex items-start gap-2" data-id="element-5496">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-5497" />
                        <span data-id="element-5498">Tu menú digital con fotos y precios</span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-5499">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-5500" />
                        <span data-id="element-5501">Código QR para mesas o redes</span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-5502">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-5503" />
                        <span data-id="element-5504">Link propio de tu restaurante</span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-5505">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-5506" />
                        <span data-id="element-5507">Configuración inicial incluida</span>
                      </li>
                    </ul>
                    <div className="pt-2 border-t border-white/5" data-id="element-5508">
                      <p className="text-[10px] uppercase tracking-wider text-white/30 font-medium" data-id="element-5509">
                        Ideal para
                      </p>
                      <p className="text-xs text-white/50" data-id="element-5510">
                        Restaurantes que quieren mostrar su menú de forma
                        profesional
                      </p>
                    </div>
                  </div>

                  {/* Fase 2 */}
                  <div className="bg-[#C9A34E]/5 border border-[#C9A34E]/20 rounded-2xl p-5 space-y-3 relative" data-id="element-5511">
                    <div className="absolute -top-2.5 right-4 bg-[#C9A34E] text-[#0B0B0B] text-[10px] font-bold px-3 py-0.5 rounded-full uppercase tracking-wider" data-id="element-5512">
                      Popular
                    </div>
                    <div className="flex items-center gap-2" data-id="element-5513">
                      <UtensilsCrossed className="w-5 h-5 text-[#C9A34E]" data-id="element-5514" />
                      <h4 className="font-bold text-white text-sm" data-id="element-5515">
                        Menú Digital Fase 2
                      </h4>
                    </div>
                    <p className="text-xs text-white/50 leading-relaxed" data-id="element-5516">
                      Menú + sistema de pedidos real.
                    </p>
                    <ul className="space-y-2 text-sm text-white/70" data-id="element-5517">
                      <li className="flex items-start gap-2" data-id="element-5518">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-5519" />
                        <span data-id="element-5520">Todo lo de Fase 1</span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-5521">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-5522" />
                        <span data-id="element-5523">Pedidos directos desde el menú</span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-5524">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-5525" />
                        <span data-id="element-5526">Pantalla de cocina (KDS)</span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-5527">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-5528" />
                        <span data-id="element-5529">Reportes básicos de ventas</span>
                      </li>
                    </ul>
                    <div className="pt-2 border-t border-[#C9A34E]/10" data-id="element-5530">
                      <p className="text-[10px] uppercase tracking-wider text-white/30 font-medium" data-id="element-5531">
                        Ideal para
                      </p>
                      <p className="text-xs text-white/50" data-id="element-5532">
                        Restaurantes que quieren recibir pedidos organizados y
                        controlar su cocina
                      </p>
                    </div>
                  </div>

                  {/* Completo */}
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-3" data-id="element-5533">
                    <div className="flex items-center gap-2" data-id="element-5534">
                      <BarChart3 className="w-5 h-5 text-[#C9A34E]" data-id="element-5535" />
                      <h4 className="font-bold text-white text-sm" data-id="element-5536">
                        Menú Digital Completo
                      </h4>
                    </div>
                    <p className="text-xs text-white/50 leading-relaxed" data-id="element-5537">
                      El sistema integral para crecer.
                    </p>
                    <ul className="space-y-2 text-sm text-white/70" data-id="element-5538">
                      <li className="flex items-start gap-2" data-id="element-5539">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-5540" />
                        <span data-id="element-5541">Todo lo de Fase 2</span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-5542">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-5543" />
                        <span data-id="element-5544">Reportes avanzados y métricas</span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-5545">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-5546" />
                        <span data-id="element-5547">Programa de lealtad para clientes</span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-5548">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-5549" />
                        <span data-id="element-5550">Inventario y control de stock</span>
                      </li>
                    </ul>
                    <div className="pt-2 border-t border-white/5" data-id="element-5551">
                      <p className="text-[10px] uppercase tracking-wider text-white/30 font-medium" data-id="element-5552">
                        Ideal para
                      </p>
                      <p className="text-xs text-white/50" data-id="element-5553">
                        Restaurantes que quieren el paquete completo: operación,
                        datos y fidelización
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Marketplace Connection */}
              <div className="bg-gradient-to-br from-[#C9A34E]/10 to-transparent border border-[#C9A34E]/20 rounded-2xl p-6" data-id="element-5554">
                <h3 className="text-lg font-bold text-[#C9A34E] mb-3 flex items-center gap-2" data-id="element-5555">
                  <Crown className="w-5 h-5" data-id="element-5556" />
                  Marketplace NOIZA — Más visibilidad para tu restaurante
                </h3>
                <p className="text-white/70 leading-relaxed mb-4" data-id="element-5557">
                  Además de tu menú digital propio, al tener cualquier plan
                  activo con NOIZA tu restaurante aparece automáticamente en
                  nuestro{' '}
                  <strong className="text-white" data-id="element-5558">
                    Marketplace gastronómico
                  </strong>{' '}
                  en <strong className="text-white" data-id="element-5559">noizacr.com</strong>. Esto
                  significa que clientes nuevos que no te conocen pueden
                  descubrirte, ver tu menú y hacerte pedidos directamente.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-5560">
                  <div className="flex items-start gap-2" data-id="element-5561">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-5562" />
                    <span className="text-sm text-white/70" data-id="element-5563">
                      Tu restaurante listado en el marketplace de NOIZA
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-5564">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-5565" />
                    <span className="text-sm text-white/70" data-id="element-5566">
                      Clientes nuevos te descubren sin que hagas nada
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-5567">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-5568" />
                    <span className="text-sm text-white/70" data-id="element-5569">
                      Pedidos directos desde la página del marketplace
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-5570">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-5571" />
                    <span className="text-sm text-white/70" data-id="element-5572">
                      Sin comisiones extra — ya está incluido en tu plan
                    </span>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-white/5 rounded-xl border border-white/5" data-id="element-5573">
                  <p className="text-xs text-white/50" data-id="element-5574">
                    <strong className="text-white/70" data-id="element-5575">Requisito:</strong> Para
                    aparecer en el marketplace, tu restaurante debe tener un
                    menú digital activo con NOIZA (cualquier plan). Así
                    garantizamos que todos los restaurantes listados ofrecen una
                    experiencia digital de calidad a los clientes.
                  </p>
                </div>
              </div>

              {/* CTA */}
              <div className="text-center pt-4" data-id="element-5576">
                <button onClick={() => {
              setShowHowItWorks(false);
              handleNext();
            }} className="px-8 py-4 bg-[#C9A34E] text-[#0B0B0B] rounded-2xl font-bold text-lg hover:bg-[#d4b365] transition-colors inline-flex items-center gap-2" data-id="element-5577">
                  Aplicar ahora <ChevronRight className="w-5 h-5" data-id="element-5578" />
                </button>
                <p className="text-white/40 text-xs mt-3" data-id="element-5579">
                  Aplicar es gratis y sin compromiso. Nuestro equipo te contacta
                  para explicarte todo.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
  const renderHero = () => <motion.div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 text-center" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0,
    y: -50
  }} data-id="element-5580">
      {/* Logo Area */}
      <motion.div className="mb-8 relative" initial={{
      scale: 0.8,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} transition={{
      duration: 0.8,
      ease: 'easeOut'
    }} data-id="element-5581">
        <div className="absolute inset-0 bg-[#C9A34E] blur-[60px] opacity-30 rounded-full" data-id="element-5582" />
        <h1 className="text-5xl md:text-7xl font-black text-white tracking-tighter relative z-10" style={{
        textShadow: '0 0 20px rgba(201,163,78,0.5)'
      }} data-id="element-5583">
          NOIZA
        </h1>
      </motion.div>

      {/* Headlines */}
      <motion.h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white max-w-4xl leading-tight mb-6" initial={{
      y: 20,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      delay: 0.2,
      duration: 0.8
    }} data-id="element-5584">
        Tu restaurante ya no necesita solo un menú.{' '}
        <span className="text-[#C9A34E]" data-id="element-5585">Necesita un sistema.</span>
      </motion.h2>

      <motion.p className="text-lg md:text-xl text-white/70 max-w-2xl mb-12" initial={{
      y: 20,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      delay: 0.4,
      duration: 0.8
    }} data-id="element-5586">
        Aplica para unirte al ecosistema digital más avanzado para restaurantes.
        Pedidos, operación, KDS y crecimiento en una sola plataforma.
      </motion.p>

      {/* CTAs */}
      <motion.div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto mb-16" initial={{
      y: 20,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      delay: 0.6,
      duration: 0.8
    }} data-id="element-5587">
        <button onClick={handleNext} className="relative group px-8 py-4 bg-[#C9A34E] text-[#0B0B0B] rounded-2xl font-bold text-lg overflow-hidden transition-transform hover:scale-105" data-id="element-5588">
          <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" data-id="element-5589" />
          <span className="relative z-10 flex items-center justify-center gap-2" data-id="element-5590">
            Aplicar ahora <ChevronRight className="w-5 h-5" data-id="element-5591" />
          </span>
        </button>
        <button onClick={() => setShowHowItWorks(true)} className="px-8 py-4 bg-white/5 hover:bg-white/10 text-white border border-white/10 rounded-2xl font-bold text-lg transition-all backdrop-blur-sm" data-id="element-5592">
          Ver cómo funciona
        </button>
      </motion.div>

      {/* Trust Pills */}
      <motion.div className="flex flex-wrap justify-center gap-3 max-w-3xl" initial="hidden" animate="visible" variants={{
      visible: {
        transition: {
          staggerChildren: 0.1,
          delayChildren: 0.8
        }
      }
    }} data-id="element-5593">
        {['Pedidos por WhatsApp', 'Tracking en tiempo real', 'KDS y operación', 'Marketplace gastronómico', 'Tecnología para restaurantes'].map((pill, i) => <motion.div key={i} variants={{
        hidden: {
          opacity: 0,
          y: 10
        },
        visible: {
          opacity: 1,
          y: 0
        }
      }} className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white/60 text-sm backdrop-blur-md" data-id="element-5594">
            {pill}
          </motion.div>)}
      </motion.div>
    </motion.div>;
  const renderFormStep = () => {
    const slideVariants = {
      enter: {
        x: 50,
        opacity: 0
      },
      center: {
        x: 0,
        opacity: 1
      },
      exit: {
        x: -50,
        opacity: 0
      }
    };
    return <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-12" data-id="element-5595">
        <motion.div className="w-full max-w-xl bg-[#141414]/80 backdrop-blur-2xl border border-white/10 rounded-3xl shadow-2xl overflow-hidden" initial={{
        scale: 0.9,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} data-id="element-5596">
          {/* Progress Bar */}
          <div className="h-1.5 w-full bg-white/5" data-id="element-5597">
            <motion.div className="h-full bg-[#C9A34E]" initial={{
            width: 0
          }} animate={{
            width: `${step / 5 * 100}%`
          }} transition={{
            type: 'spring',
            damping: 20
          }} data-id="element-5598" />
          </div>

          <div className="p-6 md:p-10" data-id="element-5599">
            {/* Header */}
            <div className="flex items-center mb-8" data-id="element-5600">
              <button onClick={handleBack} className="p-2 -ml-2 text-white/50 hover:text-white transition-colors" data-id="element-5601">
                <ArrowLeft className="w-6 h-6" data-id="element-5602" />
              </button>
              <div className="ml-auto text-sm font-bold text-[#C9A34E]" data-id="element-5603">
                Paso {step} de 5
              </div>
            </div>

            {/* Content */}
            <AnimatePresence mode="wait" data-id="element-5604">
              <motion.div key={step} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
              type: 'spring',
              damping: 25,
              stiffness: 200
            }} className="min-h-[300px]" data-id="element-5605">
                {step === 1 && <div className="space-y-6" data-id="element-5606">
                    <h3 className="text-2xl font-bold text-white mb-6" data-id="element-5607">
                      Empecemos con lo básico
                    </h3>
                    <div data-id="element-5608">
                      <label className="block text-sm font-medium text-white/60 mb-2" data-id="element-5609">
                        Tu nombre completo
                      </label>
                      <input type="text" value={formData.owner_name} onChange={e => setFormData({
                    ...formData,
                    owner_name: e.target.value
                  })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-4 text-white text-lg focus:outline-none focus:border-[#C9A34E] focus:ring-1 focus:ring-[#C9A34E] transition-all" placeholder="Ej. Juan Pérez" autoFocus data-id="element-5610" />
                    </div>
                    <div data-id="element-5611">
                      <label className="block text-sm font-medium text-white/60 mb-2" data-id="element-5612">
                        Nombre del restaurante
                      </label>
                      <input type="text" value={formData.restaurant_name} onChange={e => setFormData({
                    ...formData,
                    restaurant_name: e.target.value
                  })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-4 text-white text-lg focus:outline-none focus:border-[#C9A34E] focus:ring-1 focus:ring-[#C9A34E] transition-all" placeholder="Ej. La Pizzería" data-id="element-5613" />
                    </div>
                  </div>}

                {step === 2 && <div className="space-y-6" data-id="element-5614">
                    <h3 className="text-2xl font-bold text-white mb-6" data-id="element-5615">
                      ¿Cómo te contactamos?
                    </h3>
                    <div data-id="element-5616">
                      <label className="block text-sm font-medium text-white/60 mb-2" data-id="element-5617">
                        Número de WhatsApp
                      </label>
                      <input type="tel" value={formData.whatsapp} onChange={e => setFormData({
                    ...formData,
                    whatsapp: e.target.value
                  })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-4 text-white text-lg focus:outline-none focus:border-[#C9A34E] focus:ring-1 focus:ring-[#C9A34E] transition-all" placeholder="88888888" autoFocus data-id="element-5618" />
                    </div>
                    <div data-id="element-5619">
                      <label className="block text-sm font-medium text-white/60 mb-2" data-id="element-5620">
                        Instagram o Facebook (Opcional)
                      </label>
                      <input type="text" value={formData.social_handle} onChange={e => setFormData({
                    ...formData,
                    social_handle: e.target.value
                  })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-4 text-white text-lg focus:outline-none focus:border-[#C9A34E] focus:ring-1 focus:ring-[#C9A34E] transition-all" placeholder="@turestaurante" data-id="element-5621" />
                    </div>
                  </div>}

                {step === 3 && <div className="space-y-6" data-id="element-5622">
                    <h3 className="text-2xl font-bold text-white mb-6" data-id="element-5623">
                      ¿Qué tipo de negocio tienes?
                    </h3>
                    <div className="grid grid-cols-2 gap-3" data-id="element-5624">
                      {(['Restaurante', 'Cafetería', 'Pizzería', 'Comida rápida', 'Panadería', 'Otro'] as BusinessType[]).map(type => <button key={type} onClick={() => setFormData({
                    ...formData,
                    business_type: type
                  })} className={`p-4 rounded-xl border text-left transition-all ${formData.business_type === type ? 'bg-[#C9A34E]/10 border-[#C9A34E] text-[#C9A34E]' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}`} data-id="element-5625">
                          <span className="font-medium" data-id="element-5626">{type}</span>
                        </button>)}
                    </div>
                  </div>}

                {step === 4 && <div className="space-y-8" data-id="element-5627">
                    <h3 className="text-2xl font-bold text-white mb-6" data-id="element-5628">
                      Operación actual
                    </h3>

                    <div data-id="element-5629">
                      <label className="block text-sm font-medium text-white/60 mb-3" data-id="element-5630">
                        ¿Ya reciben pedidos por WhatsApp?
                      </label>
                      <div className="grid grid-cols-2 gap-3" data-id="element-5631">
                        <button onClick={() => setFormData({
                      ...formData,
                      uses_whatsapp_orders: true
                    })} className={`p-4 rounded-xl border text-center transition-all ${formData.uses_whatsapp_orders === true ? 'bg-[#C9A34E]/10 border-[#C9A34E] text-[#C9A34E]' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}`} data-id="element-5632">
                          Sí
                        </button>
                        <button onClick={() => setFormData({
                      ...formData,
                      uses_whatsapp_orders: false
                    })} className={`p-4 rounded-xl border text-center transition-all ${formData.uses_whatsapp_orders === false ? 'bg-[#C9A34E]/10 border-[#C9A34E] text-[#C9A34E]' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}`} data-id="element-5633">
                          No
                        </button>
                      </div>
                    </div>

                    <div data-id="element-5634">
                      <label className="block text-sm font-medium text-white/60 mb-3" data-id="element-5635">
                        ¿Tienen menú digital hoy?
                      </label>
                      <div className="grid grid-cols-2 gap-3" data-id="element-5636">
                        <button onClick={() => setFormData({
                      ...formData,
                      has_digital_menu: true
                    })} className={`p-4 rounded-xl border text-center transition-all ${formData.has_digital_menu === true ? 'bg-[#C9A34E]/10 border-[#C9A34E] text-[#C9A34E]' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}`} data-id="element-5637">
                          Sí
                        </button>
                        <button onClick={() => setFormData({
                      ...formData,
                      has_digital_menu: false
                    })} className={`p-4 rounded-xl border text-center transition-all ${formData.has_digital_menu === false ? 'bg-[#C9A34E]/10 border-[#C9A34E] text-[#C9A34E]' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}`} data-id="element-5638">
                          No
                        </button>
                      </div>
                    </div>
                  </div>}

                {step === 5 && <div className="space-y-6" data-id="element-5639">
                    <h3 className="text-2xl font-bold text-white mb-2" data-id="element-5640">
                      ¿Qué les gustaría mejorar primero?
                    </h3>
                    <p className="text-white/50 text-sm mb-6" data-id="element-5641">
                      Puedes seleccionar varias opciones
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-5642">
                      {(['Más pedidos', 'Mejor control de cocina', 'Menú digital', 'Delivery / express', 'Orden y operación', 'Todo lo anterior'] as MainGoal[]).map(goal => {
                    const isSelected = formData.main_goal.includes(goal);
                    return <button key={goal} onClick={() => {
                      if (goal === 'Todo lo anterior') {
                        setFormData({
                          ...formData,
                          main_goal: ['Todo lo anterior']
                        });
                      } else {
                        const newGoals = isSelected ? formData.main_goal.filter(g => g !== goal) : [...formData.main_goal.filter(g => g !== 'Todo lo anterior'), goal];
                        setFormData({
                          ...formData,
                          main_goal: newGoals
                        });
                      }
                    }} className={`p-4 rounded-xl border text-left transition-all ${isSelected ? 'bg-[#C9A34E]/10 border-[#C9A34E] text-[#C9A34E]' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}`} data-id="element-5643">
                            <span className="font-medium text-sm" data-id="element-5644">{goal}</span>
                          </button>;
                  })}
                    </div>
                  </div>}
              </motion.div>
            </AnimatePresence>

            {/* Footer Actions */}
            <div className="mt-10 pt-6 border-t border-white/10" data-id="element-5645">
              {error && <p className="text-red-400 text-sm mb-4 text-center" data-id="element-5646">{error}</p>}

              {step < 5 ? <button onClick={handleNext} disabled={!isStepValid()} className="w-full py-4 bg-[#C9A34E] text-[#0B0B0B] rounded-xl font-bold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#d4b365]" data-id="element-5647">
                  Continuar
                </button> : <button onClick={handleSubmit} disabled={!isStepValid() || isSubmitting} className="w-full py-4 bg-[#C9A34E] text-[#0B0B0B] rounded-xl font-bold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#d4b365] flex items-center justify-center gap-2" data-id="element-5648">
                  {isSubmitting ? <>
                      <Loader2 className="w-5 h-5 animate-spin" data-id="element-5649" /> Enviando...
                    </> : 'Enviar solicitud'}
                </button>}
            </div>
          </div>
        </motion.div>
      </div>;
  };
  const renderSuccess = () => <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 text-center" data-id="element-5650">
      <motion.div className="w-full max-w-md bg-[#141414]/80 backdrop-blur-2xl border border-white/10 rounded-3xl p-10 shadow-2xl" initial={{
      scale: 0.9,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} transition={{
      type: 'spring',
      damping: 25,
      stiffness: 200
    }} data-id="element-5651">
        <motion.div className="w-20 h-20 bg-[#C9A34E]/20 rounded-full flex items-center justify-center mx-auto mb-6" initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        delay: 0.2,
        type: 'spring'
      }} data-id="element-5652">
          <CheckCircle2 className="w-10 h-10 text-[#C9A34E]" data-id="element-5653" />
        </motion.div>

        <h2 className="text-3xl font-bold text-white mb-4" data-id="element-5654">
          Recibimos tu solicitud
        </h2>
        <p className="text-white/70 mb-8 leading-relaxed" data-id="element-5655">
          Si tu restaurante encaja con el ecosistema NOIZA, nuestro equipo se
          pondrá en contacto contigo muy pronto.
        </p>

        <a href="https://wa.me/50688888888" target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 w-full py-4 bg-[#25D366] text-white rounded-xl font-bold text-lg hover:bg-[#20bd5a] transition-colors" data-id="element-5656">
          <MessageCircle className="w-5 h-5" data-id="element-5657" />
          Hablar con NOIZA
        </a>
      </motion.div>
    </div>;
  return <div className="min-h-screen bg-[#0B0B0B] font-sans selection:bg-[#C9A34E]/30 text-white" data-id="element-5658">
      {renderFloatingBackground()}
      {renderHowItWorksModal()}

      <AnimatePresence mode="wait" data-id="element-5659">
        {step === 0 && <motion.div key="hero" className="w-full" data-id="element-5660">
            {renderHero()}
          </motion.div>}
        {step > 0 && step < 6 && <motion.div key="form" className="w-full" data-id="element-5661">
            {renderFormStep()}
          </motion.div>}
        {step === 6 && <motion.div key="success" className="w-full" data-id="element-5662">
            {renderSuccess()}
          </motion.div>}
      </AnimatePresence>
    </div>;
}